import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { getBidCenterDetails, getPDPInitData } from "@/api/PDP";
import { setListingId } from "@/bidFeature/propertyDetailSlice";
import {
  enableOwnItNow,
  fetchPDPBidCenterDataSuccess,
  fetchPDPInitialLoadSuccess,
  fetchPDPLatestBidCenterData,
  setOfferData,
  setSelectedBidAmount,
} from "@/redux/PDP/pdpSlice";
import { getLatestLisiting } from "@/api/savedPreference";
import { checkLoginStatus } from "@/api/myProfile";
import { isPhoneNoVerified } from "@/api/bid";
import {
  getEventCategory,
  trackApiCall,
  trackButtonClick,
} from "@/utils/analytics";

const PropertyButtons = ({
  propertyDetails,
  propertyListingDetails,
  fcProxyBidEnabled,
  fcProxyBidCutOffAvailable,
  userRegisteredForFcProxyBid,
  isBestBidAvailable,
  sellerFinalNegotiationResponse,
  isHnbEnded,
  cifCompleted,
  userId,
  isPhoneNumberVerified,
}) => {
  const navigate = useNavigate("");
  const dispatch = useDispatch();

  // Helper function to render a button
  const getBidDetailsFromApi = async (id) => {
    try {
      let eventCategory = getEventCategory();

      trackButtonClick({
        eventName: "user_type_check",
        eventCategory: eventCategory,
        eventSource: "Place bid click",
      });

      const loginResponse = await checkLoginStatus();

      if (loginResponse) {
        const isPhoneVerifiedResponse = await isPhoneNoVerified(
          loginResponse.primaryPhone
        );

        trackApiCall({
          eventName: "user_type_check_success",
          eventCategory: eventCategory,
          eventSource: "Place bid click api call",
        });

        const data = await getBidCenterDetails(id.listingId);
        const details = await getPDPInitData(id.listingId);

        trackButtonClick({
          eventName: "place_bid_click",
          eventCategory: eventCategory,
          eventSource: "Place bid click",
          eventLabel: `listingId: ${id.listingId} | Source: Save List Drawer`,
          eventParams: { source: "save_list_drawer" },
        });

        dispatch(fetchPDPBidCenterDataSuccess(data));
        dispatch(fetchPDPLatestBidCenterData(id.listingId));
        dispatch(fetchPDPInitialLoadSuccess(details));
        dispatch(setListingId(id));
        dispatch(setSelectedBidAmount(null));
        dispatch(enableOwnItNow(false));

        document.body.style.overflow = "";
        document.body.style.padding = "";
        navigate("/bid");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const navigateToOffers = async (id, propertyId, buttonLabel) => {
    try {
      // if(!isPhoneNumberVerified){
      //   return alert("Please verify Phone number")
      // }
      const data = await getLatestLisiting({ propertyId: propertyId });
      let offerType =
        buttonLabel === "Place Offer" ? "PLACE_OFFER" : "PLACE_BACKUP_OFFER";
      const state = {
        listingId: id,
        userId,
        offerAmount: data?.minimumOfferAmount,
        offerType,
      };
      dispatch(setOfferData(state))
      navigate("/offer", { state: state });

      document.body.style.overflow = "";
      document.body.style.padding = "";
    } catch (error) {
      console.log(error);
    }
  };

  const renderButton = (buttonLabel, isDisabled = false) => (
    <button
      className="btn btn-sm btn-thm"
      disabled={isDisabled}
      onClick={() => {
        if (buttonLabel.includes("Offer")) {
          navigateToOffers(
            propertyListingDetails?.listingId,
            propertyDetails?.id,
            buttonLabel
          );
        } else {
          getBidDetailsFromApi(propertyListingDetails);
        }
      }}
    >
      {buttonLabel}
    </button>
  );

  // Memoized function to check if buttons should be rendered
  const shouldRenderButtons = useMemo(() => {
    return (
      // Foreclosure Conditions
      (propertyDetails?.commonAttributes?.propCategory === "FORECLOSURE" &&
        fcProxyBidEnabled &&
        propertyDetails?.commonAttributes?.status !== "AUCTION_EVENT_ENDED" &&
        fcProxyBidCutOffAvailable &&
        (userRegisteredForFcProxyBid || !userRegisteredForFcProxyBid)) ||
      // Auction Conditions
      (propertyListingDetails?.listingType === "AUCN" &&
        (["ACTV", "PACT", "REVIEW", "SPND"].includes(
          propertyDetails?.commonAttributes?.status
        ) ||
          cifCompleted === "Y")) ||
      // Transaction Listing Conditions
      (propertyListingDetails?.listingType === "TRNL" &&
        ["ACTV", "SPND"].includes(propertyDetails?.commonAttributes?.status))
    );
  }, [
    propertyDetails,
    propertyListingDetails,
    fcProxyBidEnabled,
    fcProxyBidCutOffAvailable,
    userRegisteredForFcProxyBid,
    cifCompleted,
  ]);

  // Return null early if no buttons are needed
  if (!shouldRenderButtons) return null;

  return (
    <div id="buttonsContainer" className="bid-button-container">
      {/** Handle foreclosure conditions */}
      {propertyDetails?.commonAttributes?.propCategory === "FORECLOSURE" &&
        fcProxyBidEnabled &&
        propertyDetails?.commonAttributes?.status !== "AUCTION_EVENT_ENDED" &&
        fcProxyBidCutOffAvailable &&
        (userRegisteredForFcProxyBid
          ? renderButton("Place Proxy Bid")
          : renderButton("Register to Proxy Bid", true))}

      {/** Handle auction conditions */}
      {propertyListingDetails?.listingType === "AUCN" && (
        <>
          {propertyDetails?.commonAttributes?.status === "ACTV" &&
            renderButton("Place Bid")}
          {propertyDetails?.commonAttributes?.status === "PACT" &&
            renderButton("Register to Bid")}
          {/* {cifCompleted === "Y" && renderButton("Registration completed", true)} */}
          {propertyDetails?.commonAttributes?.status === "REVIEW" && (
            <>
              {isBestBidAvailable
                ? renderButton("Place Best Bid")
                : sellerFinalNegotiationResponse?.sellerFinalNegotiationAvailable
                ? renderButton(
                    `Confirm Offer for $${sellerFinalNegotiationResponse?.solicitationAmount}`,
                    true
                  )
                : sellerFinalNegotiationResponse?.isSellerFinalNegotiationAcceptedAlready
                ? renderButton("Confirm Offer", true)
                : renderButton("Place Backup Bid")}
            </>
          )}
          {propertyDetails?.commonAttributes?.status === "SPND" &&
            (isBestBidAvailable
              ? renderButton("Place Best Bid")
              : renderButton("Place Backup Bid"))}
        </>
      )}

      {/** Handle transaction listing type conditions */}
      {propertyListingDetails?.listingType === "TRNL" && (
        <>
          {propertyDetails?.commonAttributes?.status === "ACTV" &&
            (isHnbEnded
              ? renderButton("Place Backup Offer")
              : renderButton("Place Offer"))}
          {propertyDetails?.commonAttributes?.status === "SPND" &&
            renderButton("Place Backup Offer")}
        </>
      )}
    </div>
  );
};

export default PropertyButtons;
